// Settings.js

import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  FormControlLabel,
  Paper,
  Switch,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Alert,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import EditIcon from '@mui/icons-material/Edit';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import { useSearchParams } from 'react-router-dom';
import SidebarFooter from '../components/common/SidebarFooter';
import LeftSidebar from '../components/common/LeftSidebar';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { isEmailFeatureEnabled } from '../utils/outputRegistry';
import { contactEmailUsage } from '../services/functionTriggers';
import { formatText } from '../components/common/TextFormatterProvider';
import {
  canonicalizeEmail,
  isSuppressionCleanupIncomplete,
  CLEANUP_INCOMPLETE_MESSAGE,
  watch as watchContacts,
  create as createContact,
  updateDisplayName,
  updateEmail,
  deleteContact,
  reactivate as reactivateContact,
} from '../services/emailContacts';

// Sections a `/settings?section=` link may open directly.
const SETTINGS_SECTIONS = ['display', 'decodeAgent', 'uploads', 'emailContacts'];
const CONTACTS_LOADING = { status: 'loading', contacts: [], error: null };

const CONTACT_STATUS_META = {
  active: { label: 'Active', color: 'success' },
  suppressed: { label: 'Suppressed', color: 'warning' },
  deleted: { label: 'Deleted', color: 'default' },
};

const CONTACT_GROUP_ORDER = ['active', 'suppressed', 'deleted'];

// The provider suppression cleanup marker, in plain words. Decode removes a
// bounce/complaint suppression at the provider before a contact can be used
// again, so a contact can be suppressed locally while that removal is still in
// progress or has failed and is being retried.
function cleanupCaption(contact) {
  const cleanup = contact.provider_suppression_cleanup;
  if (!cleanup || !cleanup.status) return null;
  if (cleanup.status === 'pending') {
    return `Decode is removing the provider suppression for ${cleanup.email || contact.email}.`;
  }
  if (cleanup.status === 'failed') {
    return `Removing the provider suppression for ${cleanup.email || contact.email} did not succeed yet — Decode will retry.`;
  }
  return `Provider suppression for ${cleanup.email || contact.email} has been removed.`;
}

// One contact row: name, status, address, suppression cleanup state, and
// status-appropriate actions.
function ContactRow({
  contact,
  onEditName,
  onEditEmail,
  onDelete,
  onReactivate,
}) {
  const status = contact.status || 'active';
  const meta = CONTACT_STATUS_META[status] || CONTACT_STATUS_META.active;
  const editable = status !== 'deleted';
  const cleanupIncomplete = isSuppressionCleanupIncomplete(contact);
  const cleanupText = cleanupCaption(contact);
  return (
    <ListItem
      divider
      sx={{ borderRadius: 1 }}
      secondaryAction={(
        <Stack direction="row" spacing={0.5}>
          {editable && (
            <Tooltip title="Edit display name" arrow>
              <IconButton edge="end" size="small" aria-label="Edit display name" onClick={() => onEditName(contact)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {editable && (
            <Tooltip title="Edit email address" arrow>
              <IconButton edge="end" size="small" aria-label="Edit email address" onClick={() => onEditEmail(contact)}>
                <AlternateEmailIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {editable && (
            <Tooltip title="Delete contact" arrow>
              <IconButton edge="end" size="small" aria-label="Delete contact" onClick={() => onDelete(contact)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {status !== 'active' && (
            <Tooltip title={cleanupIncomplete ? CLEANUP_INCOMPLETE_MESSAGE : 'Reactivate contact'} arrow>
              {/* A disabled button emits no events, so the tooltip needs a wrapper. */}
              <span>
                <IconButton
                  edge="end"
                  size="small"
                  aria-label="Reactivate contact"
                  disabled={cleanupIncomplete}
                  onClick={() => onReactivate(contact)}
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}
    >
      <ListItemText
        primaryTypographyProps={{ component: 'div' }}
        secondaryTypographyProps={{ component: 'div' }}
        primary={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={600}>{contact.display_name}</Typography>
            <Chip label={meta.label} color={meta.color} size="small" variant="outlined" />
          </Box>
        )}
        secondary={(
          <>
            <Typography variant="body2" color="text.secondary" component="div">
              {contact.email}
            </Typography>
            {status === 'suppressed' && contact.suppression_reason && (
              <Typography variant="caption" color="text.secondary" component="div">
                Suppressed: {String(contact.suppression_reason).replace(/_/g, ' ')}
              </Typography>
            )}
            {cleanupText && (
              <Typography variant="caption" color="text.secondary" component="div">
                {cleanupText}
              </Typography>
            )}
          </>
        )}
      />
    </ListItem>
  );
}

// Create-contact dialog. Delegates validation to the service and surfaces the
// idempotent 'exists' and 'reactivation_required' outcomes instead of silently
// creating or reactivating.
function CreateContactDialog({ open, companyName, actorUid, onClose, onCreated, onRequestReactivate }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [reactivation, setReactivation] = useState(null);
  const creationAttempt = useRef(null);
  const [verificationPending, setVerificationPending] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setEmail('');
      setSaving(false);
      setError(null);
      setInfo(null);
      setReactivation(null);
      creationAttempt.current = null;
      setVerificationPending(false);
    }
  }, [open]);

  const handleCreate = async () => {
    setError(null);
    setInfo(null);
    setReactivation(null);
    // The shared canonicalizer decides validity, so the browser never accepts
    // an address the backend would reject (and never silently converts one).
    try {
      canonicalizeEmail(email);
    } catch (e) {
      setError(e.message);
      return;
    }
    setSaving(true);
    try {
      if (!creationAttempt.current) creationAttempt.current = crypto.randomUUID();
      const result = await createContact(companyName, actorUid, name, email, creationAttempt.current);
      setVerificationPending(false);
      if (result.status === 'created') {
        onCreated(`Added ${result.contact.display_name}.`);
      } else if (result.status === 'exists') {
        setInfo('A contact with this email address already exists and is active.');
      } else if (result.status === 'reactivation_required') {
        setReactivation(result.contact);
      }
    } catch (e) {
      setError(e.message);
      setVerificationPending(e.code === 'contact-save-unverified');
      if (e.code !== 'contact-save-unverified') creationAttempt.current = null;
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add email contact</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {info && <Alert severity="info">{info}</Alert>}
          {reactivation && (
            <Alert
              severity="warning"
              action={(
                <Button color="inherit" size="small" onClick={() => onRequestReactivate(reactivation)}>
                  Reactivate
                </Button>
              )}
            >
              A {reactivation.status} contact with this email address already exists. Reactivate it to send email to
              this address again.
            </Alert>
          )}
          <TextField
            autoFocus
            label="Display name"
            fullWidth
            disabled={saving || verificationPending}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="Email address"
            type="email"
            fullWidth
            disabled={saving || verificationPending}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={saving || !name.trim() || !email.trim()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {verificationPending ? 'Check and retry' : 'Add contact'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// In-place display-name edit. Does not change the destination address and never
// touches Functions or Triggers.
function NameEditDialog({ contact, companyName, actorUid, onClose, onDone }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (contact) {
      setName(contact.display_name || '');
      setSaving(false);
      setError(null);
    }
  }, [contact]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateDisplayName(companyName, actorUid, contact.id, name);
      onDone('Display name updated.');
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(contact)} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit display name</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Renaming a contact does not change its email address.
          </Typography>
          <TextField
            autoFocus
            label="Display name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !name.trim()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// What a change to this contact reaches, read while its dialog is open: the
// published Functions that email it and the automations that run them. The
// contact is the object, so the change simply applies from their next run —
// but the person is told which runs those are before making it.
function useContactEmailUsage(companyName, contact) {
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    setUsage(null);
    if (!contact?.id || !companyName) return undefined;
    let cancelled = false;
    contactEmailUsage(companyName, contact.id)
      .then((result) => { if (!cancelled) setUsage(result); })
      .catch((err) => {
        console.error('Failed to list the functions that email this contact:', err);
        if (!cancelled) setUsage({ error: true });
      });
    return () => { cancelled = true; };
  }, [companyName, contact?.id]);
  return usage;
}

function ContactUsageNote({ usage, consequence }) {
  if (usage === null) {
    return <Typography variant="body2" color="text.secondary">Checking which Functions email this contact…</Typography>;
  }
  if (usage.error) {
    return <Alert severity="warning">The Functions that email this contact could not be listed.</Alert>;
  }
  if (usage.functions.length === 0) return null;
  const names = usage.functions.map((entry) => formatText(entry.name)).join(', ');
  const automations = usage.automations > 0
    ? ` and ${usage.automations} automation${usage.automations === 1 ? '' : 's'} that run${usage.automations === 1 ? 's' : ''} them`
    : '';
  return (
    <Alert severity="info">
      {`This contact receives email from ${names}${automations}. ${consequence}`}
    </Alert>
  );
}

// Address correction, in place. The contact keeps its identity, so every
// Function that already sends to this contact simply picks up the new address —
// nothing is migrated, invalidated, or disabled.
function EmailEditDialog({ contact, companyName, actorUid, onClose, onDone }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [verificationPending, setVerificationPending] = useState(false);
  const usage = useContactEmailUsage(companyName, contact);

  useEffect(() => {
    if (contact) {
      setEmail(contact.email || '');
      setBusy(false);
      setError(null);
      setVerificationPending(false);
    }
  }, [contact]);

  const handleSave = async () => {
    setError(null);
    try {
      canonicalizeEmail(email);
    } catch (e) {
      setError(e.message);
      return;
    }
    setBusy(true);
    try {
      await updateEmail(companyName, actorUid, contact.id, email, contact.email_revision);
      onDone('Email address updated.');
    } catch (e) {
      setError(e.message);
      setVerificationPending(e.code === 'contact-save-unverified');
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(contact)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit email address</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Update the address for <strong>{contact?.display_name}</strong>. The contact keeps its identity, so
            Function Email Outputs that already use it send to the new address from their next run. Already-submitted
            and historical email records keep the original address.
          </Typography>
          <ContactUsageNote usage={usage} consequence="The new address will be used from their next run." />
          <TextField
            autoFocus
            label="Email address"
            type="email"
            fullWidth
            disabled={busy || verificationPending}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={busy || !email.trim() || email === contact?.email}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {verificationPending ? 'Check and retry' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Soft-delete. Deleting a contact touches contact fields only: any Email Output
// still configured with it fails at run time until the contact is restored or a
// different recipient is chosen in the Function.
function DeleteContactDialog({ contact, companyName, actorUid, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const usage = useContactEmailUsage(companyName, contact);

  useEffect(() => {
    if (contact) {
      setBusy(false);
      setError(null);
    }
  }, [contact]);

  const handleDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteContact(companyName, actorUid, contact.id);
      onDone('Contact deleted.');
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(contact)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete contact</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2">
            Delete <strong>{contact?.display_name}</strong> ({contact?.email})? This is a soft-delete and can be
            reversed with Reactivate.
          </Typography>
          <ContactUsageNote
            usage={usage}
            consequence="Their emails will fail until each Function's Email Output is edited and published again."
          />
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Any Email Output configured to send to this contact will fail until the contact is restored or replaced
              in that Function. The Functions themselves keep running.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Already-submitted and historical email records are unchanged.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Delete contact
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Explicit, confirmed reactivation. Unavailable while the provider suppression
// cleanup is still pending or has failed, and never re-enables Trigger Email.
function ReactivateDialog({ contact, companyName, actorUid, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const cleanupIncomplete = isSuppressionCleanupIncomplete(contact);
  // `contact` is the live document, so it is a new object on every stream
  // update; only opening for a contact resets the dialog, not those updates.
  const contactId = contact?.id || contact?.contact_id || null;

  useEffect(() => {
    if (contactId) {
      setBusy(false);
      setError(null);
    }
  }, [contactId]);

  const handleReactivate = async () => {
    setBusy(true);
    setError(null);
    try {
      await reactivateContact(companyName, actorUid, contact.id || contact.contact_id, { confirm: true });
      onDone('Contact reactivated.');
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(contact)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reactivate contact</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2">
            Reactivate <strong>{contact?.display_name}</strong> ({contact?.email})? The contact becomes active again
            and can be selected as an email recipient.
          </Typography>
          {cleanupIncomplete ? (
            <Alert severity="warning">{CLEANUP_INCOMPLETE_MESSAGE}</Alert>
          ) : (
            <Alert severity="info">
              Reactivating does not re-enable Email on any Trigger. You must turn Trigger Email back on manually where
              required.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={handleReactivate}
          variant="contained"
          disabled={busy || cleanupIncomplete}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Reactivate
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Settings() {
  const { currentUser, companyName, isOrganisationAdmin } = useAuth();
  const { showTooltips, setShowTooltips } = useDashboard();
  const [searchParams] = useSearchParams();
  // `?section=` is read once, when the page opens (a link from an Email Output
  // editor lands on Email Contacts); switching sections afterwards is local.
  const [activeSection, setActiveSection] = useState(() => {
    const requested = searchParams.get('section');
    if (!SETTINGS_SECTIONS.includes(requested)) return 'display';
    if (requested === 'emailContacts' && !isEmailFeatureEnabled()) return 'display';
    return requested;
  });
  const [agentCustomInstructions, setAgentCustomInstructions] = useState('');
  // One company-doc fetch loads every company-level setting (agent
  // instructions + workspace auto-triage), so one flag gates both sections.
  const [companySettingsLoaded, setCompanySettingsLoaded] = useState(false);
  // A refused company-document read: both sections show this instead of their
  // controls, which would otherwise offer defaults to save over the real values.
  const [companySettingsError, setCompanySettingsError] = useState(false);
  const [agentSaving, setAgentSaving] = useState(false);
  const [agentAlert, setAgentAlert] = useState(null);
  const [workspaceAutoTriage, setWorkspaceAutoTriage] = useState(false);
  const [uploadsAlert, setUploadsAlert] = useState(null);
  const [uploadsSaving, setUploadsSaving] = useState(false);

  const AGENT_INSTRUCTIONS_MAX_LENGTH = 4000;

  // --- Email Contacts section (feature-flagged, non-authoritative UI) ---
  const emailFeatureEnabled = isEmailFeatureEnabled();
  const actorUid = currentUser?.uid;
  // The contact list streams while the section is on screen, so a change from
  // another session (or the backend finishing a suppression cleanup) shows up
  // live and a mutation needs no re-read. A failed stream is finished, so
  // leaving and returning, or Retry, opens a fresh one.
  const [contactsState, setContactsState] = useState(CONTACTS_LOADING);
  const [contactsAttempt, setContactsAttempt] = useState(0);
  const contacts = contactsState.contacts;
  const contactsReady = contactsState.status === 'ready';
  const [contactsAlert, setContactsAlert] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [nameEditContact, setNameEditContact] = useState(null);
  const [emailEditContact, setEmailEditContact] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reactivateTarget, setReactivateTarget] = useState(null);

  useEffect(() => {
    if (!emailFeatureEnabled || activeSection !== 'emailContacts' || !companyName) return undefined;
    const unsubscribe = watchContacts(
      companyName,
      (rows) => setContactsState({ status: 'ready', contacts: rows, error: null }),
      (error) => {
        console.error('Error watching email contacts:', error);
        setContactsState({ status: 'error', contacts: [], error });
      },
    );
    return () => {
      unsubscribe();
      setContactsState(CONTACTS_LOADING);
    };
  }, [emailFeatureEnabled, activeSection, companyName, contactsAttempt]);

  const handleContactMutationDone = (message) => {
    setCreateOpen(false);
    setNameEditContact(null);
    setEmailEditContact(null);
    setDeleteTarget(null);
    setReactivateTarget(null);
    setContactsAlert({ severity: 'success', message });
  };

  const handleRequestReactivate = (contact) => {
    setCreateOpen(false);
    setReactivateTarget(contact);
  };

  // The Reactivate dialog follows the live document, so a suppression cleanup
  // that completes while it is open enables its button without reopening.
  const liveReactivateTarget = reactivateTarget
    && (contacts.find((contact) => contact.id === reactivateTarget.id) || reactivateTarget);

  useEffect(() => {
    if (!companyName) return;
    const fetchCompanySettings = async () => {
      setCompanySettingsError(false);
      try {
        const companyDoc = await getDoc(doc(db, 'users', companyName));
        if (companyDoc.exists()) {
          setAgentCustomInstructions(companyDoc.data().agent_custom_instructions || '');
          setWorkspaceAutoTriage(companyDoc.data().workspace_auto_triage === true);
        }
      } catch (err) {
        console.error('Error loading company settings:', err);
        setCompanySettingsError(true);
      } finally {
        setCompanySettingsLoaded(true);
      }
    };
    fetchCompanySettings();
  }, [companyName]);

  const handleToggleWorkspaceAutoTriage = async () => {
    const next = !workspaceAutoTriage;
    setWorkspaceAutoTriage(next);   // optimistic — the switch answers immediately
    setUploadsSaving(true);
    setUploadsAlert(null);
    try {
      await updateDoc(doc(db, 'users', companyName), { workspace_auto_triage: next });
    } catch (e) {
      setWorkspaceAutoTriage(!next);   // revert on failure
      setUploadsAlert({ severity: 'error', message: `Failed to save: ${e.message}` });
    }
    setUploadsSaving(false);
  };

  const handleSaveAgentInstructions = async () => {
    setAgentSaving(true);
    setAgentAlert(null);
    try {
      await updateDoc(doc(db, 'users', companyName), {
        agent_custom_instructions: agentCustomInstructions.slice(0, AGENT_INSTRUCTIONS_MAX_LENGTH),
      });
      setAgentAlert({ severity: 'success', message: 'Custom instructions saved.' });
    } catch (e) {
      setAgentAlert({ severity: 'error', message: `Failed to save: ${e.message}` });
    }
    setAgentSaving(false);
  };

  return (
    <Box 
      sx={{ 
        height: '100%', 
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          height: '100%',
          flexGrow: 1,
          m: 0,
          width: '100%',
          display: 'flex',
        }}
      >
        {/* Left Sidebar */}
        <LeftSidebar>
          <Box
            sx={{
              p: 2,
              pt: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, pl: 1 }}>
              Settings
            </Typography>
            
            <List component="nav">
              <ListItemButton
                selected={activeSection === 'display'}
                onClick={() => setActiveSection('display')}
                sx={{ 
                  borderRadius: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(78, 133, 235, 0.12)',
                    '&:hover': {
                      backgroundColor: 'rgba(78, 133, 235, 0.18)',
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <DisplaySettingsIcon color={activeSection === 'display' ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText 
                  primary="Display Settings" 
                  primaryTypographyProps={{ 
                    variant: 'body2',
                    fontWeight: activeSection === 'display' ? 600 : 400
                  }}
                />
              </ListItemButton>
              <ListItemButton
                selected={activeSection === 'decodeAgent'}
                onClick={() => setActiveSection('decodeAgent')}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(78, 133, 235, 0.12)',
                    '&:hover': {
                      backgroundColor: 'rgba(78, 133, 235, 0.18)',
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <SmartToyIcon color={activeSection === 'decodeAgent' ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText
                  primary="Decode Agent"
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: activeSection === 'decodeAgent' ? 600 : 400
                  }}
                />
              </ListItemButton>
              <ListItemButton
                selected={activeSection === 'uploads'}
                onClick={() => setActiveSection('uploads')}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(78, 133, 235, 0.12)',
                    '&:hover': {
                      backgroundColor: 'rgba(78, 133, 235, 0.18)',
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CloudUploadIcon color={activeSection === 'uploads' ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText
                  primary="Uploads"
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: activeSection === 'uploads' ? 600 : 400
                  }}
                />
              </ListItemButton>
              {emailFeatureEnabled && (
                <ListItemButton
                  selected={activeSection === 'emailContacts'}
                  onClick={() => setActiveSection('emailContacts')}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(78, 133, 235, 0.12)',
                      '&:hover': {
                        backgroundColor: 'rgba(78, 133, 235, 0.18)',
                      }
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <ContactMailIcon color={activeSection === 'emailContacts' ? 'primary' : 'inherit'} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email Contacts"
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: activeSection === 'emailContacts' ? 600 : 400
                    }}
                  />
                </ListItemButton>
              )}
            </List>

            {/* Spacer to push the footer to the bottom */}
            <Box sx={{ flexGrow: 1 }} />
            
            {/* Sidebar Footer */}
            <SidebarFooter page="settings" />
          </Box>
        </LeftSidebar>

        {/* Main Content Area */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'auto',
          flexGrow: 1,
          flexBasis: 0,
          minWidth: 0,
        }}>
          <Box sx={{ p: 3 }}>
            {activeSection === 'display' && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Display Settings
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showTooltips}
                        onChange={(e) => setShowTooltips(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Enable Tooltips"
                  />
                </Box>
              </Paper>
            )}
            {activeSection === 'decodeAgent' && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Decode Agent
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {isOrganisationAdmin
                    ? "Provide custom instructions for the Decode Agent. These will be appended to the agent's system prompt for all chats in your company."
                    : "Custom instructions for the Decode Agent. These are appended to the agent's system prompt for all chats in your company."}
                </Typography>
                {agentAlert && (
                  <Alert severity={agentAlert.severity} sx={{ mb: 2 }} onClose={() => setAgentAlert(null)}>
                    {agentAlert.message}
                  </Alert>
                )}
                {!companySettingsLoaded ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : companySettingsError ? (
                  <Alert severity="error">Failed to load. Please refresh the page.</Alert>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      maxRows={16}
                      disabled={!isOrganisationAdmin}
                      value={agentCustomInstructions}
                      onChange={(e) => {
                        if (e.target.value.length <= AGENT_INSTRUCTIONS_MAX_LENGTH) {
                          setAgentCustomInstructions(e.target.value);
                          // "Saved" describes the text that was written, not what is now typed.
                          setAgentAlert((prev) => (prev?.severity === 'success' ? null : prev));
                        }
                      }}
                      placeholder={isOrganisationAdmin
                        ? "e.g. Always respond in British English. When querying premiums data, exclude rows where status is 'Cancelled'."
                        : 'No custom instructions have been set for your organisation.'}
                      inputProps={{ maxLength: AGENT_INSTRUCTIONS_MAX_LENGTH }}
                      sx={{
                        mb: 1,
                        // A disabled MUI input dims its text to ~38% opacity, which
                        // would defeat the point of still showing members what the
                        // organisation's instructions are. Restore full contrast; the
                        // field stays genuinely non-editable and out of the tab order.
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: (theme) => theme.palette.text.primary,
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {isOrganisationAdmin ? (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            {agentCustomInstructions.length} / {AGENT_INSTRUCTIONS_MAX_LENGTH}
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={handleSaveAgentInstructions}
                            disabled={agentSaving}
                            size="small"
                          >
                            {agentSaving ? 'Saving...' : 'Save'}
                          </Button>
                        </>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Only organisation admins can change organisation settings.
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </Paper>
            )}
            {activeSection === 'uploads' && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  Uploads
                </Typography>
                {uploadsAlert && (
                  <Alert severity={uploadsAlert.severity} sx={{ mb: 2 }} onClose={() => setUploadsAlert(null)}>
                    {uploadsAlert.message}
                  </Alert>
                )}
                {!companySettingsLoaded ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : companySettingsError ? (
                  <Alert severity="error">Failed to load. Please refresh the page.</Alert>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          Auto-allocate workspace uploads
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          When Decode proposes exactly one destination table for a workspace upload, the
                          file is sent there automatically instead of waiting for approval. Files matched
                          to several tables — or none — still wait for review.
                        </Typography>
                      </Box>
                      <Switch
                        checked={workspaceAutoTriage}
                        onChange={handleToggleWorkspaceAutoTriage}
                        disabled={!isOrganisationAdmin || uploadsSaving}
                        color="primary"
                      />
                    </Box>
                    {!isOrganisationAdmin && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Only organisation admins can change organisation settings.
                      </Typography>
                    )}
                  </>
                )}
              </Paper>
            )}
            {emailFeatureEnabled && activeSection === 'emailContacts' && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                  <Typography variant="h5">Email Contacts</Typography>
                  <Button variant="contained" onClick={() => setCreateOpen(true)} disabled={!contactsReady}>
                    Add contact
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Manage the company contacts that Function Email Outputs can send to. A contact keeps its identity for
                  its whole life, so renaming it or correcting its address updates that contact in place and every
                  Function that uses it follows automatically.
                </Typography>
                {contactsAlert && (
                  <Alert
                    severity={contactsAlert.severity}
                    sx={{ mb: 2 }}
                    onClose={() => setContactsAlert(null)}
                  >
                    {contactsAlert.message}
                  </Alert>
                )}
                {contactsState.status === 'loading' ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : contactsState.status === 'error' ? (
                  <Alert
                    severity="error"
                    action={(
                      <Button color="inherit" size="small" onClick={() => setContactsAttempt((n) => n + 1)}>
                        Retry
                      </Button>
                    )}
                  >
                    Failed to load contacts: {contactsState.error?.message}
                  </Alert>
                ) : contacts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No contacts yet. Add a contact to send Function email.
                  </Typography>
                ) : (
                  <Stack spacing={3}>
                    {CONTACT_GROUP_ORDER.map((status) => {
                      const group = contacts.filter((c) => (c.status || 'active') === status);
                      if (group.length === 0) return null;
                      return (
                        <Box key={status}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            {CONTACT_STATUS_META[status].label} ({group.length})
                          </Typography>
                          <List disablePadding>
                            {group.map((contact) => (
                              <ContactRow
                                key={contact.id}
                                contact={contact}
                                onEditName={setNameEditContact}
                                onEditEmail={setEmailEditContact}
                                onDelete={setDeleteTarget}
                                onReactivate={setReactivateTarget}
                              />
                            ))}
                          </List>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            )}
          </Box>
        </Box>
      </Box>
      {emailFeatureEnabled && (
        <>
          <CreateContactDialog
            open={createOpen}
            companyName={companyName}
            actorUid={actorUid}
            onClose={() => setCreateOpen(false)}
            onCreated={handleContactMutationDone}
            onRequestReactivate={handleRequestReactivate}
          />
          <NameEditDialog
            contact={nameEditContact}
            companyName={companyName}
            actorUid={actorUid}
            onClose={() => setNameEditContact(null)}
            onDone={handleContactMutationDone}
          />
          <EmailEditDialog
            contact={emailEditContact}
            companyName={companyName}
            actorUid={actorUid}
            onClose={() => setEmailEditContact(null)}
            onDone={handleContactMutationDone}
          />
          <DeleteContactDialog
            contact={deleteTarget}
            companyName={companyName}
            actorUid={actorUid}
            onClose={() => setDeleteTarget(null)}
            onDone={handleContactMutationDone}
          />
          <ReactivateDialog
            contact={liveReactivateTarget}
            companyName={companyName}
            actorUid={actorUid}
            onClose={() => setReactivateTarget(null)}
            onDone={handleContactMutationDone}
          />
        </>
      )}
    </Box>
  );
}

export default Settings;
